export type CharacterUnderstandingKind =
  | 'feeling'
  | 'trait'
  | 'motivation'
  | 'response-to-challenge'
  | 'character-change';

export type CharacterEvidenceKind =
  | 'challenge'
  | 'action'
  | 'dialogue'
  | 'feeling'
  | 'outcome';

export interface CharacterGroveStoryPage {
  readonly id: string;
  readonly text: string;
}

export interface CharacterGroveStory {
  readonly id: string;
  readonly title: string;
  readonly pages: readonly CharacterGroveStoryPage[];
}

export interface CharacterGroveCharacter {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly challenge: string;
  readonly inspectPrompt: string;
}

export interface CharacterInsightChoice {
  readonly id: string;
  readonly text: string;
  readonly kind: CharacterUnderstandingKind;
}

export interface CharacterEvidenceMoment {
  readonly id: string;
  readonly storyPageId: string;
  readonly text: string;
  readonly kind: CharacterEvidenceKind;
}

export interface CharacterGroveHint {
  readonly stage: 1 | 2 | 3 | 4;
  readonly message: string;
  readonly focusInsightIds?: readonly string[];
  readonly focusEvidenceIds?: readonly string[];
}

export interface CharacterGroveConfig {
  readonly id: string;
  readonly skillId: string;
  readonly skillKind: CharacterUnderstandingKind;
  readonly title: string;
  readonly story: CharacterGroveStory;
  readonly character: CharacterGroveCharacter;
  readonly insightChoices: readonly CharacterInsightChoice[];
  readonly evidenceMoments: readonly CharacterEvidenceMoment[];
  readonly correctInsightId: string;
  readonly supportingEvidenceIds: readonly string[];
  readonly requiredEvidenceCount: number;
  readonly hints: readonly CharacterGroveHint[];
  readonly completionWorldEvent: string;
  readonly successMessage: string;
}

function requireUniqueIds(ids: readonly string[], label: string, challengeId: string) {
  if (ids.some(id => !id.trim()) || new Set(ids).size !== ids.length) {
    throw new Error(`Character Grove "${challengeId}" needs unique, non-empty ${label} ids.`);
  }
}

/** Reject malformed curriculum data before a future production view binds it. */
export function validateCharacterGroveConfig(config: CharacterGroveConfig) {
  if (![config.id, config.skillId, config.story.id, config.character.id,
    config.completionWorldEvent].every(value => value.trim())) {
    throw new Error('Character Grove identifiers must not be empty.');
  }
  if (config.story.pages.length < 2) {
    throw new RangeError(`Character Grove "${config.id}" needs at least two readable story pages.`);
  }
  if (config.insightChoices.length < 3 || config.evidenceMoments.length < 3) {
    throw new RangeError(`Character Grove "${config.id}" needs at least three insights and evidence moments.`);
  }

  const pageIds = config.story.pages.map(page => page.id);
  const insightIds = config.insightChoices.map(choice => choice.id);
  const evidenceIds = config.evidenceMoments.map(moment => moment.id);
  requireUniqueIds(pageIds, 'story page', config.id);
  requireUniqueIds(insightIds, 'insight', config.id);
  requireUniqueIds(evidenceIds, 'evidence', config.id);

  if (!insightIds.includes(config.correctInsightId)) {
    throw new Error(`Character Grove "${config.id}" has no matching correct insight.`);
  }
  if (config.evidenceMoments.some(moment => !pageIds.includes(moment.storyPageId))) {
    throw new Error(`Character Grove "${config.id}" contains evidence from an unknown story page.`);
  }
  if (config.supportingEvidenceIds.length === 0
      || config.supportingEvidenceIds.some(id => !evidenceIds.includes(id))
      || new Set(config.supportingEvidenceIds).size !== config.supportingEvidenceIds.length) {
    throw new Error(`Character Grove "${config.id}" has invalid supporting evidence.`);
  }
  if (!Number.isInteger(config.requiredEvidenceCount)
      || config.requiredEvidenceCount < 1
      || config.requiredEvidenceCount > config.supportingEvidenceIds.length) {
    throw new RangeError(`Character Grove "${config.id}" has an invalid evidence requirement.`);
  }
  if (config.hints.length < 4 || config.hints.some((hint, index) => hint.stage !== index + 1)) {
    throw new RangeError(`Character Grove "${config.id}" needs four ordered progressive hints.`);
  }
  for (const hint of config.hints) {
    if (hint.focusInsightIds?.some(id => !insightIds.includes(id))
        || hint.focusEvidenceIds?.some(id => !evidenceIds.includes(id))) {
      throw new Error(`Character Grove "${config.id}" has a hint with an unknown focus id.`);
    }
  }
  return config;
}
